import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Localization from 'expo-localization';
import i18n, { InitOptions, StringMap, TOptions } from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next, TFunction } from 'react-i18next';

const I18N_CACHE_PREFIX = '@w3lcome/i18n-cache:';

type BackendResponse = {
  status: number;
  data: string;
};

type BackendRequestCallback = (error: Error | null, response?: BackendResponse) => void;

function getCacheKey(url: string) {
  return `${I18N_CACHE_PREFIX}${url.split('?')[0]}`;
}

async function getCachedTranslation(url: string) {
  const cachedTranslation = await AsyncStorage.getItem(getCacheKey(url));

  if (!cachedTranslation) {
    return null;
  }

  try {
    JSON.parse(cachedTranslation);
    return cachedTranslation;
  } catch {
    return null;
  }
}

async function respondWithCachedTranslation(url: string, callback: BackendRequestCallback) {
  try {
    const cachedTranslation = await getCachedTranslation(url);

    if (!cachedTranslation) {
      return false;
    }

    callback(null, { status: 200, data: cachedTranslation });
    return true;
  } catch {
    return false;
  }
}

async function requestWithCache(
  _options: unknown,
  url: string,
  _payload: unknown,
  callback: BackendRequestCallback
) {
  try {
    const response = await fetch(url);
    const data = await response.text();

    if (!response.ok) {
      if (await respondWithCachedTranslation(url, callback)) {
        return;
      }

      callback(null, { status: response.status, data });
      return;
    }

    JSON.parse(data);
    await AsyncStorage.setItem(getCacheKey(url), data);
    callback(null, { status: response.status, data });
  } catch (error) {
    if (await respondWithCachedTranslation(url, callback)) {
      return;
    }

    callback(error instanceof Error ? error : new Error('Failed to load translations'));
  }
}

const languageDetector = {
  type: 'languageDetector',
  async: true,
  detect: async (language: any) => {
    const persistedLocale = await AsyncStorage.getItem('@w3lcome/language');
    if (!persistedLocale) {
      const deviceLocale = Localization.getLocales()[0]?.languageTag;
      return language(deviceLocale);
    }
    language(persistedLocale);
  },
  init: () => {},
  cacheUserLanguage: () => {},
};

export async function initI18n() {
  const translationSheetId = await AsyncStorage.getItem('@w3lcome/translationId');

  const timestamp = new Date().getTime();

  const loadPath = translationSheetId
    ? `${Constants.expoConfig?.extra?.assetsURL}/locales/${translationSheetId}/app-host/{{lng}}/translation.json?timestamp=${timestamp}`
    : `${Constants.expoConfig?.extra?.assetsURL}/locales/app-host/{{lng}}/translation.json?timestamp=${timestamp}`;

  const initConfig: InitOptions = {
    fallbackLng: 'en',
    supportedLngs: ['en', 'pt-BR'],
    compatibilityJSON: 'v3',
    debug: false,
    backend: {
      loadPath,
      crossDomain: true,
      request: requestWithCache,
    },
    cache: {
      enabled: true,
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  };

  i18n
    .use(languageDetector as any)
    .use(initReactI18next)
    .use(Backend)
    .init(initConfig);
}

initI18n();

export const translate: TFunction<'translation', undefined> = (
  key: string,
  options?: string | TOptions<StringMap> | undefined
) => i18n.t(key, options);

export default i18n;
